import { Component, Element, Event, EventEmitter, h, Host, Prop } from "@stencil/core";

export type TileVariant = "dark" | "light" | "outline" | "content-only";

/**
 * @slot (default) - tile content
 * @slot additional-header-element - additional custom header placed on the right
 */
@Component({
    tag: "uic-tile",
    styleUrl: "tile.scss"
})
export class Tile {
    @Element() host: HTMLUicTileElement;

    /** Custom icon to show to the left of the headline in the header bar */
    @Prop() readonly icon?: string;

    /** The headline/label/title displayed in the header bar */
    @Prop() readonly headline: string;

    /**
     * Whether the tile should behave like it's clickable,
     * i.e. the click event bubbles and there are css hover, active & focus states
     */
    @Prop() readonly isClickable: boolean = true;

    /**
     * Whether the tile should behave like it's dragable,
     * i.e. uses matching mouse cursor and there are css hover, active & focus states
     */
    @Prop() readonly isDraggable: boolean = false;

    /**
     * Whether the tile should be closeable with a cross icon in the header bar.
     */
    @Prop() readonly isDismissable: boolean = false;

    /**
     * Whether the tile is in a loading state or not,
     * i.e. display a loader instead of the content
     */
    @Prop() readonly isLoading: boolean = false;

    /**
     * Whether the tile is dragged right now,
     * i.e. dashed border, opacity adjusted.
     * Set this to true during movement when using a drag and drop lib.
     */
    @Prop() readonly isDragged: boolean = false;

    /**
     * Tile variant. "outline" should only be used for "buttons" to add more tiles
     */
    @Prop() readonly variant: TileVariant = "light";

    /**
     * Triggered when clicking on the cross button in the header
     * The tile has to be dissmisable to use this event
     */
    @Event() uicCloseClicked: EventEmitter;

    /**
     * Triggered when clicking on the header
     * Hint: Use the standard click event to handle body and header clicks.
     */
    @Event() uicHeaderClicked: EventEmitter;

    private handleTileClicked(event) {
        if (!this.isClickable) {
            event.stopPropagation();
        }
    }

    private handleCloseClick(event) {
        event.stopPropagation();
        this.uicCloseClicked.emit();
    }

    private handleHeaderClick() {
        this.uicHeaderClicked.emit(true);
    }

    render() {
        return (
            <Host>
                <div
                    onClick={(e) => this.handleTileClicked(e)}
                    class={{
                        "uic-tile": true,
                        "uic-tile--with-content": this.host.children.length > 0,
                        "uic-tile--clickable": this.isClickable,
                        "uic-tile--dragable": this.isDraggable,
                        "uic-tile--dragging": this.isDragged,
                        [`uic-tile--${this.variant}`]: true
                    }}
                    tabindex='1'
                >
                    {this.variant !== "content-only" && (
                        <div
                            onClick={() => this.handleHeaderClick()}
                            class={{
                                "uic-tile__header": true
                            }}
                        >
                            {this.icon && <uic-icon class='uic-tile__icon' name={this.icon}></uic-icon>}
                            <div class='uic-tile__label'>{this.headline}</div>
                            <slot name='additional-header-element'></slot>
                            {this.isDismissable && this.variant !== "outline" && (
                                <uic-icon-button
                                    class='uic-tile__close-button'
                                    variant={this.variant === "light" ? "dark" : "light"}
                                    onClick={(e) => this.handleCloseClick(e)}
                                    icon='uic:cross'
                                ></uic-icon-button>
                            )}
                            <div class='uic-tile__border'></div>
                        </div>
                    )}
                    <div class='uic-tile__content'>
                        <div
                            class={{
                                "uic-tile__content-container": true,
                                "uic-tile__content-container--hidden": this.isLoading
                            }}
                        >
                            <slot></slot>
                        </div>
                        {this.isLoading && this.variant !== "outline" && (
                            <div class='uic-tile__loader-container'>
                                <uic-loader
                                    class='uic-tile__loader'
                                    variant={this.variant === "light" || this.variant === "content-only" ? "dark" : "light"}
                                ></uic-loader>
                            </div>
                        )}
                    </div>
                </div>
            </Host>
        );
    }
}
